import tt from '@/i18n/utils/translateText';
import { EApprovalSubjectType } from '@/contexts/approvalsContext/domain/constants';
import { EOrderInfoDrawerTab } from '@/components/OrderInfoDrawer/constants';
import { joinString } from '@/utils';
import { EEntityType } from '@/contexts/referencesContext/domain/types';
import { DATE_FORMAT } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextAboutConnectedPlan } from '@/contexts/billingContext/domain/getTextAboutConnectedPlan';
import { useView } from '@partnershipsContext/public/composables';
import { EBillingPlanCode } from '@/contexts/billingContext/domain/constants';

import { ENotificationType, EResourseType } from './constants';
import {
  TOrderPayload,
  TNotificationScheme,
  TExecutorsTenderPayload,
  TPartnershipResoursePayload,
} from './types';

/**
 * Все переходы и тексты взяты из таблицы "CTX-Y События для отправки уведомлений" https://confluence.trucker.group/pages/viewpage.action?pageId=2083553338
 */

const getApprovalPath = (approvalSubjectType?: string, approvalId?: string, approvalSubjectTargetId?: string) => {
  if (!approvalSubjectType && !approvalId && !approvalSubjectTargetId) return '';

  let type: 'claims' | 'surcharges' | '' = '';

  const idParam = approvalId ? `?id=${approvalId}` : '';
  const combinedExecutionRequestIdParam = approvalSubjectTargetId && approvalSubjectType === EApprovalSubjectType.CombinedExecutionRequestExtraService
    ? `&combinedExecutionRequestId=${approvalSubjectTargetId}`
    : '';

  switch (approvalSubjectType) {
    case EApprovalSubjectType.claim: {
      type = 'claims';
      break;
    }
    case EApprovalSubjectType.extraService:
    case EApprovalSubjectType.CombinedExecutionRequestExtraService: {
      type = 'surcharges';
      break;
    }
    default:
  }

  return type
    ? `/approvals/${type}${idParam}${combinedExecutionRequestIdParam}`
    : '';
};

const getExecutionRequestPath = (executionRequestUid?: string) => {
  const executionRequestUidParam = executionRequestUid ? `?executionRequestId=${executionRequestUid}` : '';
  return `/execution_requests${executionRequestUidParam}`;
};

const getOrderPath = (payload: TOrderPayload) => {
  const {
    orderIid,
    folderSlug,
    activeTab,
    runUid,
    executionRequestUid,
    sourceType,
    combinedOrderId,
  } = payload;
  const orderIidParam = orderIid ? `orderIid=${orderIid}` : '';
  const combinedOrderIdParam = combinedOrderId ? `combinedOrderId=${combinedOrderId}` : '';
  const idParam = sourceType && sourceType === EEntityType.combinedOrder ? combinedOrderIdParam : orderIidParam;

  const folderSlugParam = folderSlug ? `folderId=${folderSlug}` : '';
  const activeTabParam = activeTab ? `activeTab=${activeTab}` : '';
  const runUidParam = runUid ? `runUid=${runUid}` : '';
  const executionRequestUidParam = executionRequestUid ? `executionRequestUid=${executionRequestUid}` : '';
  const params = joinString([idParam, folderSlugParam, activeTabParam, runUidParam, executionRequestUidParam], '&');

  return `/orders/only_orders?${params}`;
};

const getTenderStagePath = (payload: TExecutorsTenderPayload | null) => `/tenders?stageId=${payload?.tenderStageId}`;

const getResourseType = (type: EResourseType = EResourseType.vehicle) => (type === EResourseType.vehicle ? 'vehicles' : 'drivers');

const getTargetText = (payload: TPartnershipResoursePayload | null, type: EResourseType) => (type === EResourseType.vehicle
  ? `${tt('shared.vehicleShort')} ${payload?.vehicleLicensePlate}`
  : `${tt('notification.scheme.text.driver')} ${payload?.driverFullName}`);

const getStatusChengetText = (payload: TPartnershipResoursePayload | null, type: EResourseType) => {
  const status = payload?.status ? PARTNERSHIP_RESOURCE_CLIENT_STATUS_TRANSLATION.value[payload.status] : '';
  const comment = payload?.publicComment ? tt('notification.scheme.text.andAddComment') : '';

  const text = tt('notification.scheme.text.statusChanged', {
    param: {
      status,
      target: getTargetText(payload, type),
    },
  });

  return joinString([text, comment], ' ');
};

const { PARTNERSHIP_RESOURCE_CLIENT_STATUS_TRANSLATION } = useView();

const getNotificationScheme: (payload: Record<string, string> | null) => TNotificationScheme = (payload) => ({
  // partnershipsContext
  [ENotificationType.partnershipsContextNotifyAboutInvitation]: {
    title: tt('notification.scheme.title.newInvitation'),
    template: tt('notification.scheme.text.invitation', { param: { partnerName: payload?.partnerName } }),
    actions: [{
      text: tt('notification.scheme.action.showRequest'),
      path: '/partnerships/invitations?filter=incoming',
    }],
  },
  [ENotificationType.partnershipsContextCustomersNotifyAboutNewPartnership]: {
    title: tt('notification.scheme.title.newPartnership'),
    template: tt('notification.scheme.text.newPartnership', { param: { partnerName: payload?.partnerName } }),
    actions: [{
      text: tt('notification.scheme.action.partnershipDetails'),
      path: `/partnerships/partners/${payload?.partnershipId}`,
    }],
  },
  [ENotificationType.partnershipsContextExecutorsNotifyAboutNewPartnership]: {
    title: tt('notification.scheme.title.newPartnership'),
    template: tt('notification.scheme.text.newPartnership', { param: { partnerName: payload?.partnerName } }),
    actions: [{
      text: tt('notification.scheme.action.partnershipDetails'),
      path: `/partnerships/partners/${payload?.partnershipId}`,
    }],
  },
  [ENotificationType.partnershipsContextNotifyAboutPartnershipAccreditationChanges]: {
    title: tt('notification.scheme.title.accreditationChanges'),
    template: tt('notification.scheme.text.partnershipAccreditationChanges', { param: { partnerName: payload?.partnerName } }),
    actions: [],
  },
  [ENotificationType.partnershipsContextNotifyAboutNewResourceToVerify]: {
    title: tt('notification.scheme.title.newResourceToVerify'),
    template: tt('notification.scheme.text.newResourceToVerify', { param: { partnerName: payload?.partnerName } }),
    actions: [{
      text: tt('notification.scheme.action.check'),
      path: `/partnership_resources/${getResourseType(payload?.resourceType as EResourseType)}?partnershipResourceId=${payload?.resourceId}`,
    }],
  },
  [ENotificationType.partnershipsContextNotifyAboutClientStatusChangeForDriver]: {
    title: tt('notification.scheme.title.newResourceStatus'),
    template: getStatusChengetText(payload, EResourseType.driver),
    actions: [{
      text: tt('notification.scheme.action.goToDriver'),
      path: `/employees/all?employeeId=${payload?.driverId}`,
    }],
  },
  [ENotificationType.partnershipsContextNotifyAboutClientStatusChangeForVehicle]: {
    title: tt('notification.scheme.title.newResourceStatus'),
    template: getStatusChengetText(payload, EResourseType.vehicle),
    actions: [{
      text: tt('notification.scheme.action.goToVehicle'),
      path: `/vehicles?filter=${payload?.vehicleType}&vehicleId=${payload?.vehicleId}`,
    }],
  },
  [ENotificationType.partnershipsContextNotifyAboutCommentForDriverChanged]: {
    title: tt('notification.scheme.title.newResourceStatus'),
    template: tt('notification.scheme.text.commentChanged', { param: { target: getTargetText(payload, EResourseType.driver) } }),
    actions: [{
      text: tt('notification.scheme.action.goToDriver'),
      path: `/employees/all?employeeId=${payload?.driverId}`,
    }],
  },
  [ENotificationType.partnershipsContextNotifyAboutCommentForVehicleChanged]: {
    title: tt('notification.scheme.title.newResourceStatus'),
    template: tt('notification.scheme.text.commentChanged', { param: { target: getTargetText(payload, EResourseType.vehicle) } }),
    actions: [{
      text: tt('notification.scheme.action.goToVehicle'),
      path: `/vehicles?filter=${payload?.vehicleType}&vehicleId=${payload?.vehicleId}`,
    }],
  },
  // orderingContext
  [ENotificationType.orderingContextCustomersNotifyAboutCompletedOrder]: {
    title: tt('notification.scheme.title.orderCompleted'),
    template: tt('notification.scheme.text.orderCompleted', { param: { orderIid: payload?.orderIid } }),
    actions: [{
      text: tt('shared.action.goToOrder'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
      }),
    }],
  },
  // freightExecutionContext
  [ENotificationType.freightExecutionContextCustomersNotifyAboutCanceledByExecutorRequest]: {
    title: tt('notification.scheme.title.canceledByExecutorRequest'),
    template: tt('notification.scheme.text.canceledByExecutorRequest', {
      param: {
        executorName: payload?.executorName,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
      }),
    }],
  },
  [ENotificationType.freightExecutionContextCustomersNotifyAboutCompletedExecutionRequest]: {
    title: tt('executionRequestPanel.shared.requestCompleted'),
    template: tt('notification.scheme.text.completedRequest', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToOrder'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
      }),
    }],
  },
  [ENotificationType.freightExecutionContextCustomersNotifyAboutNewRequest]: {
    title: tt('notification.scheme.title.newRequest'),
    template: tt('notification.scheme.text.newRequest', {
      param: {
        executorName: payload?.executorName,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
      }),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutCanceledByCustomerRequest]: {
    title: tt('notification.requestCancelled'),
    template: tt('notification.scheme.text.canceledByCustomerRequest', {
      param: {
        customerName: payload?.customerName,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedAdditionalInformationOnTransitPoint]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedAdditionalInformationOnTransitPoint', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedAddressOnTransitPoint]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedAddressOnTransitPoint', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedCargoInformation]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedCargoInformation', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedDriverRequirements]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedDriverRequirements', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedExecutionRequestRoute]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedExecutionRequestRoute', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedExpectedArrivalTimeOnTransitPoint]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedExpectedArrivalTimeOnTransitPoint', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedInformationOnTransitPoint]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedInformationOnTransitPoint', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedVehicleRequirements]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedVehicleRequirements', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedPublicComment]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedPublicComment', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('notification.scheme.action.openFreightRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextApprovalsNotifyAboutPending]: {
    title: tt('notification.scheme.title.approvalUpdating'),
    template: tt('notification.scheme.text.approvalPending', { param: { approvalName: payload?.approvalDescription } }),
    actions: [{
      text: tt('notification.scheme.action.openApproval'),
      path: getApprovalPath(
        payload?.approvalSubjectType,
        payload?.approvalId,
        payload?.approvalSubjectTargetId,
      ),
    }],
  },
  [ENotificationType.freightExecutionContextApprovalsNotifyAboutApproval]: {
    title: tt('notification.scheme.title.approvalUpdating'),
    template: tt('notification.scheme.text.approvalApproved', { param: { approvalName: payload?.approvalDescription } }),
    actions: [{
      text: tt('notification.scheme.action.openApproval'),
      path: getApprovalPath(
        payload?.approvalSubjectType,
        payload?.approvalId,
        payload?.approvalSubjectTargetId,
      ),
    }],
  },
  [ENotificationType.freightExecutionContextApprovalsNotifyAboutRevoking]: {
    title: tt('notification.scheme.title.approvalUpdating'),
    template: tt('notification.scheme.text.approvalRevoking', { param: { approvalName: payload?.approvalDescription } }),
    actions: [{
      text: tt('notification.scheme.action.openApproval'),
      path: getApprovalPath(
        payload?.approvalSubjectType,
        payload?.approvalId,
        payload?.approvalSubjectTargetId,
      ),
    }],
  },
  [ENotificationType.freightExecutionContextApprovalsNotifyAboutMarkedAsInRevision]: {
    title: tt('notification.scheme.title.approvalUpdating'),
    template: tt('notification.scheme.text.approvalMarkedAsInRevision', { param: { approvalName: payload?.approvalDescription } }),
    actions: [{
      text: tt('notification.scheme.action.openApproval'),
      path: getApprovalPath(
        payload?.approvalSubjectType,
        payload?.approvalId,
        payload?.approvalSubjectTargetId,
      ),
    }],
  },
  [ENotificationType.freightExecutionContextApprovalsNotifyAboutRejection]: {
    title: tt('notification.scheme.title.approvalUpdating'),
    template: tt('notification.scheme.text.approvalMarkedAsInRejection', { param: { approvalName: payload?.approvalDescription } }),
    actions: [{
      text: tt('notification.scheme.action.openApproval'),
      path: getApprovalPath(
        payload?.approvalSubjectType,
        payload?.approvalId,
        payload?.approvalSubjectTargetId,
      ),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutNewExecutionRequest]: {
    title: tt('notification.scheme.title.newExecutionRequests'),
    template: tt('notification.scheme.text.newExecutionRequest', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedExtraServicePrice]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedExtraServicePrice', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedPrice]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt('notification.scheme.text.changedPrice', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.freightExecutionContextExecutorsNotifyAboutChangedPriceDueToDistanceChange]: {
    title: tt('notification.scheme.title.requestConditionsChanged'),
    template: tt(
      'notification.scheme.text.changedPriceDueToDistanceChange',
      { param: { executionRequestUid: payload?.executionRequestUid } },
    ),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  // runExecutionContext
  [ENotificationType.runExecutionContextCustomersNotifyAboutPlannedRun]: {
    title: tt('notification.scheme.title.assignmentTransportResources'),
    template: tt('notification.scheme.text.plannedRun', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutReplacedTransport]: {
    title: tt('notification.scheme.title.assignmentTransportResources'),
    template: tt('notification.scheme.text.replacedTransport', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('shared.action.goToRequest'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutNewRunIncident]: {
    title: tt('notification.scheme.title.newRunIncident'),
    template: tt('notification.scheme.text.newRunIncident', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutResolvedRunIncident]: {
    title: tt('notification.scheme.title.runIncidentResolved'),
    template: tt('notification.scheme.text.resolvedRunIncident', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutCompletedRun]: {
    title: tt('notification.scheme.title.runCompleted'),
    template: tt('notification.scheme.text.completedRun', {
      param: {
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutCompletionOfCanceledRun]: {
    title: tt('notification.scheme.title.runCompleted'),
    template: tt('notification.scheme.text.completionOfCanceledRun', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutCancelationOfCompletedRun]: {
    title: tt('notification.scheme.title.runCancellation'),
    template: tt('notification.scheme.text.cancelationOfCompletedRun', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextCustomersNotifyAboutCanceledRun]: {
    title: tt('notification.scheme.title.runCancellation'),
    template: tt('notification.scheme.text.canceledRun', {
      param: {
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerExecutionRequests,
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      }),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutPlannedRun]: {
    title: tt('notification.scheme.title.assignmentTransportResources'),
    template: tt('notification.scheme.text.plannedRun', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutReplacedTransport]: {
    title: tt('notification.scheme.title.assignmentTransportResources'),
    template: tt('notification.scheme.text.replacedTransport', { param: { executionRequestUid: payload?.executionRequestUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutRunOnDocumentsPreparation]: {
    title: tt('notification.scheme.title.prepareDocumentsForRun'),
    template: tt('notification.scheme.text.runOnDocumentsPreparation', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutFinishedRunExecution]: {
    title: tt('notification.scheme.title.runCompleted'),
    template: tt('notification.scheme.text.finishedRunExecution', {
      param: {
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutNewRunIncident]: {
    title: tt('notification.scheme.title.newRunIncident'),
    template: tt('notification.scheme.text.newRunIncident', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutResolvedRunIncident]: {
    title: tt('notification.scheme.title.runIncidentResolved'),
    template: tt('notification.scheme.text.resolvedRunIncident', { param: { runUid: payload?.runUid } }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  [ENotificationType.runExecutionContextExecutorsNotifyAboutCompletedRun]: {
    title: tt('notification.scheme.title.runCompleted'),
    template: tt('notification.scheme.text.finishedRun', {
      param: {
        runUid: payload?.runUid,
        executionRequestUid: payload?.executionRequestUid,
      },
    }),
    actions: [{
      text: tt('freightExecution.executionRequests.shared.goToRun'),
      path: getExecutionRequestPath(payload?.executionRequestUid),
    }],
  },
  // targetOffersContext
  [ENotificationType.targetOffersContextCustomersNotifyAboutAcceptedTargetOffer]: {
    title: tt('notification.scheme.title.replyToTargetOffer'),
    template: tt('notification.scheme.text.acceptedTargetOffer', { param: { executorName: payload?.executorName } }),
    actions: [{
      text: tt('notification.scheme.action.goToOffer'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
        sourceType: payload?.sourceType as EEntityType,
      }),
    }],
  },
  [ENotificationType.targetOffersContextCustomersNotifyAboutRejectedTargetOffer]: {
    title: tt('notification.scheme.title.replyToTargetOffer'),
    template: tt('notification.scheme.text.rejectedTargetOffer', { param: { executorName: payload?.executorName } }),
    actions: [{
      text: tt('notification.scheme.action.goToOffer'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
        sourceType: payload?.sourceType as EEntityType,
      }),
    }],
  },
  [ENotificationType.targetOffersContextCustomersNotifyAboutExpiredTargetOffer]: {
    title: tt('notification.scheme.title.replyToTargetOffer'),
    template: tt('notification.scheme.text.expiredTargetOffer', { param: { executorName: payload?.executorName } }),
    actions: [{
      text: tt('notification.scheme.action.goToOffer'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
        sourceType: payload?.sourceType as EEntityType,
      }),
    }],
  },
  [ENotificationType.targetOffersContextExecutorsNotifyAboutNewTargetOffer]: {
    title: tt('notification.scheme.title.newTargetOffers'),
    template: tt('notification.scheme.text.newTargetOffer'),
    actions: [{
      text: tt('notification.scheme.action.goToOffer'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=TargetOffersContext::TargetOffer`,
    }],
  },
  // spotOffersContext
  [ENotificationType.spotOffersContextCustomersNotifyAboutAcceptedSpotOffer]: {
    title: tt('notification.scheme.title.newDataAboutLookup'),
    template: tt('notification.scheme.text.acceptedSpotOffer'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.spotOffersContextCustomersNotifyAboutExpiredSpotOffer]: {
    title: tt('notification.scheme.title.newDataAboutLookup'),
    template: tt('notification.scheme.text.expiredSpotOffer'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.spotOffersContextExecutorsNotifyAboutPublishedSpotOffer]: {
    title: tt('notification.scheme.title.default'),
    template: tt('notification.scheme.text.publishedSpotOffer'),
    actions: [{
      text: tt('notification.scheme.action.viewConditions'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=SpotOffersContext::SpotOffer`,
    }],
  },
  // reductionsContext
  [ENotificationType.reductionsContextContextCustomersNotifyAboutExpiredReduction]: {
    title: tt('notification.scheme.title.newDataAboutLookup'),
    template: tt('notification.scheme.text.expiredReduction'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextContextCustomersNotifyAboutPlacedBid]: {
    title: tt('notification.scheme.title.biddingUpdates'),
    template: tt('notification.scheme.text.placedBid'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextCustomersNotifyAboutRevokedBid]: {
    title: tt('notification.scheme.title.biddingUpdates'),
    template: tt('notification.scheme.text.revokedBid'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextCustomersNotifyAboutExpiredBid]: {
    title: tt('notification.scheme.title.biddingUpdates'),
    template: tt('notification.scheme.text.revokedBid'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextContextCustomersNotifyAboutFinishedByTimeReduction]: {
    title: tt('notification.scheme.title.newDataAboutLookup'),
    template: tt('notification.scheme.text.finishedByTimeReduction'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextContextCustomersNotifyAboutFinishedByBuyNowReduction]: {
    title: tt('notification.scheme.title.newDataAboutLookup'),
    template: tt('notification.scheme.text.finishedByBuyNowReduction', { param: { executorsLookupId: payload?.executorsLookupId } }),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutReactivatedBid]: {
    title: tt('notification.scheme.title.biddingUpdates'),
    template: tt('reduction.shared.bidReactivated'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutKnockedOutBid]: {
    title: tt('notification.scheme.title.biddingUpdates'),
    template: tt('reduction.shared.knockoutBid'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutPublishedReduction]: {
    title: tt('notification.scheme.title.default'),
    template: tt('notification.scheme.text.publishedReduction'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutManuallyFinishedReduction]: {
    title: tt('notification.scheme.title.lookupResults'),
    template: tt('notification.scheme.text.manuallyFinishedReduction'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutFinishedByTimeReduction]: {
    title: tt('notification.scheme.title.lookupResults'),
    template: tt('notification.scheme.text.finishedByTimeOurReduction'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  [ENotificationType.reductionsContextContextExecutorsNotifyAboutFinishedByBuyNowReduction]: {
    title: tt('notification.scheme.title.lookupResults'),
    template: tt('notification.scheme.text.finishedByBuyNowReduction', { param: { executorsLookupId: payload?.executorsLookupId } }),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=ReductionsContext::Reduction`,
    }],
  },
  // freeOfferingsContext
  [ENotificationType.freeOfferingsContextExecutorsNotifyAboutFreeOfferingOrganized]: {
    title: tt('notification.scheme.title.default'),
    template: tt('notification.scheme.text.organizedFreeOffering'),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&drawerOrderId=${payload?.executorsLookupId}&lookupMethodType=FreeOfferingsContext::FreeOffering`,
    }],
  },
  // executorsLookupContext
  [ENotificationType.executorsLookupContextCustomersNotifyAboutFailedStageStart]: {
    title: tt('notification.scheme.title.strategyUpdate'),
    template: tt('notification.scheme.text.failedStageStart'),
    actions: [{
      text: tt('notification.scheme.action.goToLookups'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  [ENotificationType.executorsLookupContextCustomersNotifyAboutFailedAutomationStepExecution]: {
    title: tt('notification.scheme.title.strategyUpdate'),
    template: tt('notification.scheme.text.failedAutomationStepExecution'),
    actions: [{
      text: tt('notification.scheme.action.goToLookups'),
      path: getOrderPath({
        folderSlug: payload?.folderSlug,
        orderIid: payload?.orderIid,
        activeTab: EOrderInfoDrawerTab.orderInfoDrawerLookups,
      }),
    }],
  },
  // tendersContext
  [ENotificationType.tendersContextExecutorsNotifyAboutTenderStageStart]: {
    title: tt('tenders.shared.title'),
    template: tt('notification.scheme.text.tenderStageStart', {
      param: {
        customerName: payload?.customerName,
        stageType: payload?.stageType,
        dateStart: payload?.startsAt,
        dateEnd: payload?.endsAt,
      },
    }),
    actions: [{
      text: tt('notification.scheme.action.participate'),
      path: getTenderStagePath(payload),
    }],
  },
  [ENotificationType.tendersContextExecutorsNotifyAboutTenderStageCancel]: {
    title: tt('tenders.shared.title'),
    template: tt('notification.scheme.text.tenderStageCancel', {
      param: {
        customerName: payload?.customerName,
        stageType: payload?.stageType,
        dateStart: payload?.startsAt,
        dateEnd: payload?.endsAt,
      },
    }),
    actions: [],
  },
  [ENotificationType.tendersContextExecutorsNotifyAboutTenderStagePause]: {
    title: tt('tenders.shared.title'),
    template: tt('notification.scheme.text.tenderStagePause', {
      param: {
        customerName: payload?.customerName,
        stageIid: payload?.stageIid,
      },
    }),
    actions: [{
      text: tt('notification.scheme.action.goToTender'),
      path: getTenderStagePath(payload),
    }],
  },
  [ENotificationType.tendersContextExecutorsNotifyAboutTenderMailings]: {
    title: tt('notification.scheme.title.tenderMailing'),
    template: tt('notification.scheme.text.tenderMailing', { param: { customerName: payload?.customerName } }),
    actions: [],
  },
  [ENotificationType.tenderContextExecutorsNotifyAboutTenderStageResume]: {
    title: tt('tenders.shared.title'),
    template: tt('notification.scheme.text.tenderStageResume', {
      param: {
        customerName: payload?.customerName,
        stageIid: payload?.stageIid,
      },
    }),
    additionalInfo: {
      title: tt('shared.executorComment'),
      description: payload?.comment,
    },
    actions: [{
      text: tt('notification.scheme.action.goToTender'),
      path: getTenderStagePath(payload),
    }],
  },
  [ENotificationType.tenderContextExecutorsNotifyAboutTenderStageDurationChange]: {
    title: tt('tenders.shared.title'),
    template: tt('notification.scheme.text.tenderDurationChange', {
      param: {
        customerName: payload?.customerName,
        stageType: payload?.stageType,
      },
    }),
    actions: [{
      text: tt('notification.scheme.action.findOutDateAndTime'),
      path: getTenderStagePath(payload),
    }],
  },
  [ENotificationType.executorsLookupContextExecutorsNotifyAboutNewLookups]: {
    title: tt('notification.scheme.title.newLookupsInFilters'),
    template: tt('notification.scheme.text.newLookups', { param: { filterName: payload?.filterTitle } }),
    actions: [{
      text: tt('notification.scheme.action.goToAuction'),
      path: `/executors_lookups/feed?filter=new&filterId=${payload?.filterId}`,
    }],
  },
  [ENotificationType.billingContextExecutorsNotifyAboutTerminateSpecialOffer]: {
    title: tt('notification.scheme.title.tariffDetails'),
    template: tt('notification.scheme.text.specialOfferTerminated', {
      param: {
        terminatedAt: payload?.terminatedAt
          ? formatDate(payload.terminatedAt, {
            inputFormat: DATE_FORMAT.ISOLocal,
            outputFormat: DATE_FORMAT.fullDate,
          })
          : '',
        planCode: payload?.planCode
          ? getTextAboutConnectedPlan({
            planCode: payload.planCode as EBillingPlanCode,
            withoutPrefix: true,
            withoutTariff: true,
          })
          : '',
      },
    }),
    actions: [{
      text: tt('notification.scheme.action.goToTariffSettings'),
      path: '/tenant/settings',
    }],
  },
});

export default getNotificationScheme;
